import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "./api";

let audioCtx = null;
const getCtx = () => {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  if (!audioCtx) audioCtx = new Ctx();
  if (audioCtx.state === "suspended") audioCtx.resume().catch(() => {});
  return audioCtx;
};

export function playOrderChime(loud = false) {
  try {
    const ctx = getCtx();
    if (!ctx) return;
    const notes = loud ? [880, 1175, 1568, 1175, 1568] : [880, 1175, 1568];
    notes.forEach((freq, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = "triangle";
      const t = ctx.currentTime + i * 0.16;
      o.frequency.setValueAtTime(freq, t);
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(loud ? 0.6 : 0.35, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.3);
      o.start(t);
      o.stop(t + 0.32);
    });
  } catch (e) { console.debug("Audio indisponible :", e?.message); }
}

const ACK_KEY = "stuff_ack_orders";
const readAck = () => { try { return JSON.parse(localStorage.getItem(ACK_KEY) || "[]"); } catch { return []; } };

export function useNewOrderAlert(enabled) {
  const [orders, setOrders] = useState([]);
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("stuff_sound") !== "0");
  const [ack, setAck] = useState(readAck);
  const notifiedRef = useRef(new Set());
  const soundRef = useRef(soundOn);
  soundRef.current = soundOn;

  const reload = useCallback(() => api.get("/vendor/orders").then((r) => setOrders(Array.isArray(r.data) ? r.data : [])).catch(console.error), []);

  useEffect(() => {
    if (!enabled) return undefined;
    reload();
    const id = setInterval(reload, 10000);
    const prime = () => getCtx();
    window.addEventListener("pointerdown", prime, { once: true });
    return () => { clearInterval(id); window.removeEventListener("pointerdown", prime); };
  }, [enabled, reload]);

  const pending = orders.filter((o) => o.status === "nouvelle" && !ack.includes(o.id));
  const pendingKey = pending.map((o) => o.id).join(",");

  // première alerte + notification navigateur pour chaque nouvelle commande
  useEffect(() => {
    const fresh = orders.filter((o) => pendingKey.split(",").includes(o.id) && !notifiedRef.current.has(o.id));
    if (!fresh.length) return;
    fresh.forEach((o) => notifiedRef.current.add(o.id));
    if (soundRef.current) playOrderChime(true);
    try { navigator.vibrate?.([300, 100, 300]); } catch (e) { console.debug(e); }
    if ("Notification" in window && Notification.permission === "granted" && document.hidden) {
      fresh.forEach((o) => {
        try { new Notification(`Nouvelle commande #${o.ref} 🛵`, { body: `${o.customer_name} — ${Number(o.total || 0).toFixed(2)}€ · ${o.city || ""}`, tag: o.id, icon: "/stuff-logo.png" }); } catch (e) { console.debug(e); }
      });
    }
  }, [pendingKey, orders]);

  // rappel sonore toutes les 20s + titre clignotant tant qu'une commande n'est pas prise en charge
  useEffect(() => {
    if (!pending.length) { document.title = document.title.replace(/^\(\d+\) 🔔 /, ""); return undefined; }
    const base = document.title.replace(/^\(\d+\) 🔔 /, "");
    let flip = false;
    const titleId = setInterval(() => { flip = !flip; document.title = flip ? `(${pending.length}) 🔔 Nouvelle commande !` : base; }, 1200);
    const soundId = setInterval(() => { if (soundRef.current) playOrderChime(false); }, 20000);
    return () => { clearInterval(titleId); clearInterval(soundId); document.title = base; };
  }, [pending.length]);

  const toggleSound = useCallback(() => {
    const next = !soundRef.current;
    setSoundOn(next);
    localStorage.setItem("stuff_sound", next ? "1" : "0");
    if (next) {
      playOrderChime(false);
      if ("Notification" in window && Notification.permission === "default") Notification.requestPermission().catch(() => {});
    }
    return next;
  }, []);

  const acknowledge = useCallback((ids) => {
    const all = Array.from(new Set([...readAck(), ...ids])).slice(-300);
    localStorage.setItem(ACK_KEY, JSON.stringify(all));
    setAck(all);
  }, []);

  return { orders, reload, pending, soundOn, toggleSound, acknowledge };
}
